/**
 * Experiment / feature-flag keys — OptiGrowth-decider experiments and any
 * remaining PostHog flags.
 *
 * One source of truth for flag names so frontend, backend, and PostHog UI all
 * reference the same string. When adding a new experiment, document the flag
 * in `docs/experiments/<flag-key>.md`.
 */
export const EXPERIMENT_FLAGS = {
  /**
   * welcome-screen-v2: 2-arm A/B on the Screens-page welcome screen. Server-decided
   * via the OptiGrowth decider (NOT PostHog) and consumed through
   * `useExperimentVariant`, which persists the arm write-once to
   * `company.experiments['welcome-screen-v2']` — sticky per account, forever.
   *
   * Arms: `control` (the current welcome screen, Classic/V2 per
   * VITE_USE_CLASSIC_WELCOME_SCREEN) | `b` (WelcomeScreenTest — the platform-ticker
   * screen). Anything else, and not-enrolled, renders control.
   *
   * QA preview (non-prod): `?ws=b` for short, or the generic
   * `?force_variant_welcome-screen-v2=b`.
   *
   * WHY A NEW KEY (2026-09-08): the old `welcome-screen` key is spent. Its OptiGrowth
   * sandbox record was created with arms `control`/`a` instead of `control`/`b`, the
   * code only renders the ticker for `b`, and arm names FREEZE once assignments exist.
   * The platform has no delete and `status=ended` is terminal, so the runbook's rule
   * applies: "re-running means a new key." Accounts still carrying a legacy
   * `experiments['welcome-screen']` value (5,785 on `b` in prod, from the PostHog era)
   * no longer match anything and get freshly randomized under this key.
   * See `docs/experiments/welcome-screen-v2.md`.
   */
  WELCOME_SCREEN_V2: 'welcome-screen-v2',

  /**
   * add-screen-cta-v1: 2-arm (control/treatment) copy test on the WelcomeScreen
   * "Add Screen" CTA button label. Server-decided via the OptiGrowth decider
   * (NOT PostHog) and consumed through `useExperimentVariant`. Arms: `control`
   * (current "Add Screen") | `treatment` ("Add Your First Screen"). qa3g-only
   * harness test. See `docs/experiments/add-screen-cta-v1.md`.
   */
  ADD_SCREEN_CTA: 'add-screen-cta-v1',

  /**
   * sub-update-v1: 2-arm 50/50 test on the whole subscription-update surface —
   * the plan/configure/payment wizard plus the lapsed-account paywall. Server-
   * decided via the OptiGrowth decider (NOT PostHog), `unit: company`, consumed
   * through `useBillingExperimentArm` (which wraps `useExperimentVariant` and
   * feeds the arm to the component-level switches in AppShell, BillingDialogs,
   * SettingsLayout and UserMenu).
   *
   * Arm keys: `control` (production's wizard + inactive-account dialog) | `b`
   * (`features/billing/experiment/**` — the reworked wizard + LapsedPortalBackdrop).
   * The dashboard experiment MUST use exactly these two names; any other arm
   * string logs a warning and falls through to the build default rather than
   * silently serving control. See `docs/experiments/sub-update-v1.md`.
   *
   * Reviewer override, independent of the decider: `?sub-update=b` /
   * `?sub-update=control` (sticks per browser). Generic QA override also works:
   * `?force_variant_sub-update-v1=<arm>`.
   */
  SUB_UPDATE: 'sub-update-v1',
} as const;

export type ExperimentFlagKey = (typeof EXPERIMENT_FLAGS)[keyof typeof EXPERIMENT_FLAGS];

/**
 * Surfaces where VISITOR-unit experiments (anonymous, pre-auth — OptiGrowth
 * decider keyed on the og_vid browser id, not companyId) can run. The server
 * allowlists these: `resolveVisitorExperiments(surface, visitorId)` only
 * resolves experiments whose config names the requested surface. Extend the
 * enum when a new pre-auth surface gets experiments; each entry needs a
 * `useVisitorExperiments(surface)` call site on that page.
 * Spec: claude-dev/kate/2026-07-21-optigrowth-visitor-experiments/ (harness root).
 */
export const VISITOR_EXPERIMENT_SURFACES = {
  SIGNUP_PAGE: 'SIGNUP_PAGE',
} as const;

export type VisitorExperimentSurface =
  (typeof VISITOR_EXPERIMENT_SURFACES)[keyof typeof VISITOR_EXPERIMENT_SURFACES];

/** One resolved visitor-experiment assignment: which arm this og_vid gets for `key`. */
export interface VisitorAssignment {
  key: string;
  arm: string;
}
